import { useFormContext, useWatch } from "react-hook-form";
import clsx from "clsx";
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  TagIcon,
} from "@heroicons/react/24/outline";
import Header from "../../components/layout/Header";
import LoadingScreen from "../../components/loadingScreen";
import { useUserContext } from "../../hooks/useUserContext";
import { useShippingLabelContext } from "../../hooks/useShippingLabelContext";
import { FACILITY } from "../../constants";
import type { ShippingLabelFormValues } from "../../types/forms";
import { useNavigate } from "react-router-dom";
import { routeEndpoints } from "../../routes/routeEndpoints";
import PackageDetails from "./sections/packageDetails";
import AddressDetails from "./sections/addressDetails";
import CarrierService from "./sections/carrierService";

export default function ShippingLabelPage() {
  const { user, isLoading } = useUserContext();
  const navigate = useNavigate();
  const methods = useFormContext<ShippingLabelFormValues>();
  const {
    ratesQuery: {
      mutate: calculateRates,
      data: rates,
      isIdle,
      isPending: isCalculating,
      isError,
    },
    calculatedInputs,
    setCalculatedInputs,
  } = useShippingLabelContext();

  const rateInputs = JSON.stringify(
    useWatch({
      control: methods.control,
      name: ["packageDetails", "fromAddressId"],
    }),
  );
  const canCalculate =
    !isCalculating && (isError || rateInputs !== calculatedInputs);

  const handleCalculate = async () => {
    if (!(await methods.trigger(["packageDetails", "fromAddressId"]))) {
      return;
    }
    const { fromAddressId, packageDetails } = methods.getValues();
    const fromAddress = user?.addresses.find(
      ({ id }) => id === fromAddressId,
    )?.address;
    if (!fromAddress) {
      return;
    }

    methods.setValue("rateId", "");
    setCalculatedInputs(rateInputs);
    calculateRates({
      fromAddress,
      toAddress: FACILITY.shippingAddress,
      packageDetails,
    });
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#F9FAFB] font-sans">
      <Header />
      <main className="flex-1 px-4 md:px-0 py-6 md:py-5">
        <div className="max-w-200 w-full mx-auto flex flex-col gap-5">
          <button
            type="button"
            onClick={() => navigate(`${routeEndpoints.orders}`)}
            className="flex w-fit items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors cursor-pointer"
          >
            <ArrowLeftIcon className="size-4" />
            Back
          </button>

          <div className="w-full border border-gray-200 rounded-xl bg-white md:p-5 px-4 py-6 md:mb-20 mb-8">
            <div className="flex items-center gap-3 mb-8">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-blue-50">
                <TagIcon className="size-5 text-blue-600" />
              </div>
              <h1 className="text-xl md:text-2xl font-medium text-gray-950">
                Buy shipping label
              </h1>
            </div>

            <form noValidate className="flex flex-col gap-8">
              <PackageDetails />
              <AddressDetails />

              <button
                type="button"
                onClick={handleCalculate}
                disabled={!canCalculate}
                className="h-13 w-full flex items-center justify-center gap-2 rounded-xl text-base font-medium text-white bg-blue-600 hover:bg-blue-700 cursor-pointer transition-colors disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                <ArrowPathIcon
                  className={clsx("size-5", isCalculating && "animate-spin")}
                />
                Calculate
              </button>

              {!isIdle && (
                <CarrierService
                  rates={rates ?? []}
                  isLoading={isCalculating}
                  isError={isError}
                />
              )}
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
